<script setup>
  import { ref, computed, onMounted, watch } from 'vue'
  import BcstQuizStepper from './BcstQuizStepper.vue'
  import BcstQuizQuestionCard from './BcstQuizQuestionCard.vue'
  import BcstQuizAnsweredCount from './BcstQuizAnsweredCount.vue'
  import BcstQuizResultContent from './BcstQuizResultContent.vue'
  import { useQuizProgress } from '../../composables/useQuizProgress'
  import { useTransformOwnerContext } from '../../composables/useTransformOwnerContext'
  import { TransformLearningProgressService } from '../../services/TransformLearningProgressService'
  import { handleErrors } from '../../utils/errorHandler'

  const props = defineProps({
    lessonId: { type: String, required: true },
    courseId: { type: String, required: true },
    // From store.assessmentsByLessonId[lessonId]?.id — null if no assessment found.
    assessmentId: { type: String, default: null },
    // Pass-mark percent from bcst_assessments.passThresholdPercent.
    passThreshold: { type: Number, default: 75 },
    // Seeds from bcst_lesson_progress (allow resume).
    initialCurrentQuestionIndex: { type: Number, default: 0 },
    initialDraftAnswers: { type: Object, default: () => ({}) },
    initialCompleted: { type: Boolean, default: false },
    initialScorePercent: { type: Number, default: null },
    // Real score breakdown from the last submission (persisted), so a resumed result
    // shows the exact correct/total instead of an estimate derived from scorePercent.
    initialCorrectCount: { type: Number, default: null },
    initialTotalCount: { type: Number, default: null },
    // Quiz title shown in the pass-result subtitle.
    quizTitle: { type: String, default: '' },
  })

  // Bubbled up so the lesson-detail view (which owns the full breadcrumb context) opens
  // the Results Breakdown page in a new tab.
  const emit = defineEmits(['viewBreakdown'])

  // Admin "view-as" is read-only: the interactive quiz (and the Retake CTA) is replaced
  // by a passive state — the write path is also guarded in useQuizProgress/useQuizRestart,
  // this only keeps the UI honest about it.
  const { isActive: isOwnerViewActive, targetUserName: ownerTargetName } = useTransformOwnerContext()

  // ── Result state ───────────────────────────────────────────────
  // The graded outcome, rendered in place by the inline result panel once the quiz is
  // completed (there is no completion modal — the panel is the single completion UI).
  const quizResult = ref(null)

  function buildQuizResult(score, passed, correct) {
    return {
      result: passed ? 'pass' : 'fail',
      scorePercent: score,
      correctCount: correct,
      totalCount: questions.value.length,
      passThreshold: props.passThreshold,
      quizTitle: props.quizTitle,
    }
  }

  // ── Question loading ───────────────────────────────────────────
  const questions = ref([])
  const isLoading = ref(true)
  const hasError = ref(false)

  // Normalises a question served by the gateway into the shape the player uses.
  // The gateway intentionally omits the answer key (scoring is server-side), so this
  // only maps the display fields (prompt + ordered options).
  function normalizeQuestion(raw) {
    const rawOpts = Array.isArray(raw.options) ? raw.options : []
    const options = rawOpts
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map((o, i) => ({
        id: String(o.id ?? o.optionId ?? i),
        text: o.text ?? o.label ?? o.value ?? '',
      }))

    return {
      id: raw.id,
      position: raw.position ?? 0,
      type: raw.type ?? 'single_choice',
      text: raw.prompt ?? raw.text ?? '',
      options,
    }
  }

  // ── Quiz progress ──────────────────────────────────────────────
  const {
    currentQuestionIndex,
    answers,
    isCompleted,
    isSaving,
    isAutoSaving,
    savedQuestionIds,
    saveError,
    selectAnswer,
    goToQuestion,
    restartQuiz,
    submitQuiz,
  } = useQuizProgress({
    lessonId: props.lessonId,
    courseId: props.courseId,
    initialCurrentQuestionIndex: props.initialCurrentQuestionIndex,
    initialDraftAnswers: props.initialDraftAnswers,
    initialCompleted: props.initialCompleted,
    initialScorePercent: props.initialScorePercent,
  })

  // Track which questions have been explicitly submitted (Next/Submit button).
  // Selecting an option alone does NOT count as submitted in the current session.
  // Previously-persisted answers (from draftAnswers) are considered already submitted
  // so the count reflects the user's saved progress when they return.
  const submittedQuestionIds = ref(
    new Set(Object.keys(props.initialDraftAnswers ?? {}))
  )

  // ── Derived state ──────────────────────────────────────────────
  const currentQuestion = computed(
    () => questions.value[currentQuestionIndex.value] ?? null
  )

  const isLastQuestion = computed(
    () => currentQuestionIndex.value >= questions.value.length - 1
  )

  // A question is answered when it has a scalar selection or a non-empty array
  // (multiple-choice questions store an array of selected option ids).
  function isAnswered(answer) {
    return Array.isArray(answer) ? answer.length > 0 : answer != null
  }

  const allAnswered = computed(
    () =>
      questions.value.length > 0 &&
      questions.value.every(q => isAnswered(answers.value[q.id]))
  )

  // Reflects answers CONFIRMED PERSISTED by the auto-save (not merely selected in the UI),
  // so the counter only advances after a successful save.
  const answeredCount = computed(() => savedQuestionIds.value.size)

  // The current selection: an array for multiple-choice, a scalar otherwise.
  const selectedAnswer = computed(() => {
    if (!currentQuestion.value) return null
    const value = answers.value[currentQuestion.value.id]
    if (currentQuestion.value.type === 'multiple_choice') return Array.isArray(value) ? value : []
    return value ?? null
  })

  // Result payload is computed on demand in buildQuizResult.

  // ── Navigation ─────────────────────────────────────────────────
  function prevQuestion() {
    if (currentQuestionIndex.value > 0) goToQuestion(currentQuestionIndex.value - 1)
  }

  function nextQuestion() {
    if (currentQuestion.value) {
      submittedQuestionIds.value.add(currentQuestion.value.id)
      // Reassign to keep the reactive ref alive.
      submittedQuestionIds.value = new Set(submittedQuestionIds.value)
    }
    if (!isLastQuestion.value) goToQuestion(currentQuestionIndex.value + 1)
  }

  function handleOptionSelect(optionId) {
    const q = currentQuestion.value
    if (!q) return
    if (q.type === 'multiple_choice') {
      // Toggle the option within the answer array (order-independent; scored as a set).
      const current = Array.isArray(answers.value[q.id]) ? answers.value[q.id] : []
      const next = current.includes(optionId)
        ? current.filter(id => id !== optionId)
        : [...current, optionId]
      selectAnswer(q.id, next)
    } else {
      selectAnswer(q.id, optionId)
    }
  }

  async function handleSubmit() {
    questions.value.forEach(q => submittedQuestionIds.value.add(q.id))
    submittedQuestionIds.value = new Set(submittedQuestionIds.value)
    const result = await submitQuiz(questions.value)
    if (result) {
      // isCompleted flips to true inside submitQuiz, so setting the result here reveals
      // the inline completion panel in place — no modal, no extra dialog.
      quizResult.value = buildQuizResult(result.score, result.passed, result.correct)
    }
  }

  // Retake: keep the completion panel (and its loading Retake button) on screen while the
  // reset request runs — mirrors how Submit keeps the question card visible during grading —
  // so the section never flashes blank. Only clear the result and swap to the fresh
  // questions once the server confirms the reset; on failure, leave the panel intact.
  async function retakeQuiz() {
    const restarted = await restartQuiz()
    if (!restarted) return
    quizResult.value = null
    submittedQuestionIds.value = new Set()
  }

  function handleRetake() {
    return retakeQuiz()
  }

  // Build the result payload for an already-graded quiz on (re)entry so the questions
  // area renders the inline completion panel directly.
  watch(
    [() => props.initialCompleted, () => questions.value.length],
    ([completed, hasQuestions]) => {
      if (completed && hasQuestions > 0 && quizResult.value == null) {
        submittedQuestionIds.value = new Set(questions.value.map(q => q.id))
        const score = props.initialScorePercent
        const passed = score !== null && score >= props.passThreshold
        // Prefer the persisted correct count; fall back to an estimate for legacy
        // progress docs saved before correctCount/totalCount were stored.
        const correct = props.initialCorrectCount != null
          ? props.initialCorrectCount
          : (score !== null ? Math.round((score / 100) * questions.value.length) : 0)
        quizResult.value = buildQuizResult(score, passed, correct)
      }
    },
    { immediate: true }
  )

  // ── Lifecycle ──────────────────────────────────────────────────
  onMounted(async () => {
    if (!props.assessmentId) {
      hasError.value = true
      isLoading.value = false
      return
    }

    // If already completed and the progress doc is loaded, jump straight to results.
    // Still fetch questions so retake is instant.
    try {
      const svc = TransformLearningProgressService()
      const rawQuestions = await svc.getQuizQuestions(props.assessmentId)
      questions.value = rawQuestions.map(normalizeQuestion)

      // Guard: clamp currentQuestionIndex to valid range after questions load.
      if (currentQuestionIndex.value >= questions.value.length) {
        goToQuestion(Math.max(0, questions.value.length - 1))
      }

      // If the quiz was already completed, every question is considered submitted.
      if (isCompleted.value) {
        submittedQuestionIds.value = new Set(questions.value.map(q => q.id))
      }
    } catch (err) {
      hasError.value = true
      handleErrors(`TransformLessonQuizPlayer.loadQuestions-${props.assessmentId}`, err)
    } finally {
      isLoading.value = false
    }
  })
</script>

<template>
  <div class="tlqp-wrapper">

    <!-- ── Loading ── -->
    <div v-if="isLoading" class="tlqp-state">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading quiz…</span>
      </div>
    </div>

    <!-- ── Error ── -->
    <div v-else-if="hasError" class="tlqp-state tlqp-state--error">
      <i class="bi bi-exclamation-triangle tlqp-state-icon" aria-hidden="true"></i>
      <p class="tlqp-state-text">This quiz is currently unavailable.</p>
    </div>

    <!-- ── Empty (no questions) ── -->
    <div v-else-if="!questions.length" class="tlqp-state">
      <i class="bi bi-patch-question tlqp-state-icon text-muted" aria-hidden="true"></i>
      <p class="tlqp-state-text text-muted">No questions found for this quiz.</p>
    </div>

    <template v-else>

      <!-- ── Admin view-as, quiz not taken: read-only placeholder instead of the
           interactive player (an admin must never answer on someone else's behalf). ── -->
      <div v-if="!isCompleted && isOwnerViewActive" class="tlqp-state">
        <i class="bi bi-clipboard-x tlqp-state-icon text-muted" aria-hidden="true"></i>
        <p class="tlqp-state-text text-muted">
          {{ ownerTargetName }} has not completed this quiz yet.
        </p>
      </div>

      <!-- ── Active quiz ── -->
      <!-- Shown until the quiz is graded; once completed, the inline result panel below
           replaces it in place (there is no completion modal). -->
      <div v-else-if="!isCompleted" class="tlqp-quiz">

        <!-- Answered count + stepper header -->
        <div class="tlqp-stepper-section">
          <div class="tlqp-answered-row">
            <BcstQuizAnsweredCount
              :answered="answeredCount"
              :total="questions.length" />
          </div>

          <div class="tlqp-stepper-row">
            <BcstQuizStepper
              :questions="questions"
              :answers="answers"
              :submittedQuestionIds="submittedQuestionIds"
              :currentIndex="currentQuestionIndex"
              :isCompleted="isCompleted"
              @navigate="goToQuestion" />
          </div>
        </div>

        <!-- Question card -->
        <BcstQuizQuestionCard
          v-if="currentQuestion"
          :question="currentQuestion"
          :questionIndex="currentQuestionIndex"
          :totalQuestions="questions.length"
          :selected="selectedAnswer"
          :isFirst="currentQuestionIndex === 0"
          :isLast="isLastQuestion"
          :allAnswered="allAnswered"
          :isSaving="isSaving"
          :isAutoSaving="isAutoSaving"
          :disabled="isCompleted"
          @select="handleOptionSelect"
          @previous="prevQuestion"
          @next="nextQuestion"
          @submit="handleSubmit" />

        <!-- Save error -->
        <p v-if="saveError" class="tlqp-save-error" role="alert">
          <i class="bi bi-exclamation-circle me-1" aria-hidden="true"></i>
          {{ saveError }}
        </p>

      </div>

      <!-- ── Inline result panel: the single completion UI, shown in place once graded
           (both pass and fail). ── -->
      <div v-if="isCompleted && quizResult" class="tlqp-result-panel">
        <BcstQuizResultContent
          :result="quizResult.result"
          :correctCount="quizResult.correctCount"
          :totalCount="quizResult.totalCount"
          :passThreshold="quizResult.passThreshold"
          :quizTitle="quizResult.quizTitle ?? props.quizTitle"
          :isRetaking="isSaving"
          :allowRetake="!isOwnerViewActive"
          @retake="handleRetake"
          @viewBreakdown="emit('viewBreakdown')"
        />
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  /* Outer wrapper: quiz content fills the available main-column width. */
  .tlqp-wrapper {
    width: 100%;
  }

  /* ── Loading / error states ─────────────────────────────────── */
  .tlqp-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    width: 100%;
    aspect-ratio: 16 / 9;
    border: 1px dashed var(--bs-gray-400);
    border-radius: 12px;
    background-color: var(--bs-gray-100, #f8f9fa);
    padding: 24px;
    text-align: center;
  }

  .tlqp-state--error {
    background-color: var(--bs-gray-100);
  }

  .tlqp-state-icon {
    font-size: 2.5rem;
    color: var(--bs-danger);
  }

  .tlqp-state-text {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--bs-body-color);
  }

  /* ── Active quiz layout ───────────────────────────────────── */
  .tlqp-quiz {
    display: flex;
    flex-direction: column;
    gap: 42px;
  }

  .tlqp-result-panel {
    margin-top: 32px;
    min-height: 362px;
    padding: 24px;
    border: 1px solid var(--bs-gray-300, #dee2e6);
    border-radius: 12px;
    background-color: var(--bs-white, #fff);
    /* Center the result content within the fixed-height card. */
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Stepper section: answered count above, stepper 21px below it */
  .tlqp-stepper-section {
    display: flex;
    flex-direction: column;
  }

  /* Answered count label row */
  .tlqp-answered-row {
    margin-bottom: 21px;
  }

  /* Stepper row: centered/inset horizontally for visual separation from header and question card */
  .tlqp-stepper-row {
    display: flex;
    flex-direction: column;
    padding: 8px 24px 4px;
    min-height: fit-content;
    overflow: visible;
  }

/* ── Save error ───────────────────────────────────────────── */
  .tlqp-save-error {
    font-size: 14px;
    color: var(--bs-danger);
    margin: 0;
  }
</style>
